import React, { useState, useRef, useEffect } from 'react';
import { Box, Button, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import { useSnackbar } from 'notistack';
import StarRating from '../StarComponents/StarRating';
import { AutoAwesomeOutlined, Close as CloseIcon } from '@mui/icons-material';
import MobileFeedback from './MobileFeedback';
import { UserFeedBackRatings } from '../../Controller/request';

interface FeedbackProps {
  onClose: () => void;
}

const questions = [
  "How helpful was this conversation?",
  "How likely would you suggest this to your colleagues?",
];

const Feedback: React.FC<FeedbackProps> = ({ onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmitted, setSubmitted] = useState(false);
  const [ratings, setRatings] = useState<Array<number | null>>(questions.map(() => null));
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const feedbackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (feedbackRef.current && !feedbackRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handleRatingChange = (index: number, value: number | null) => {
    const newRatings = [...ratings];
    newRatings[index] = value;
    setRatings(newRatings);
  };

  const handleSubmit = () => {
    if (ratings.every(rating => rating !== null && rating > 0)) {
      callFeedBackRatingsApi(ratings[0] as number, ratings[1] as number);
    } else {
      enqueueSnackbar('Please give rating to submit', {
        variant: 'warning',
      });
    }
  };

  const callFeedBackRatingsApi = async (rating1: number, rating2: number) => {
    const chatRating = rating1.toString();
    const recommendRating = rating2.toString();
    try {
      const sessionData = await UserFeedBackRatings({ chatRating, recommendRating });
      if (sessionData.status === 200) {
        enqueueSnackbar('Feedback submitted successfully!', {
          variant: 'success',
        });
        setSubmitted(true);
      }
    } catch (error) {
      enqueueSnackbar('Something went wrong. Please attempt this again later.', {
        variant: 'error',
      });
    }
  }

  return !isSubmitted ? (
    isSmallScreen ? (
      <MobileFeedback
        questions={questions}
        ratings={ratings}
        handleRatingChange={handleRatingChange}
        handleSubmit={handleSubmit}
        onClose={onClose}
      />
    ) : (
      <motion.div
        initial={{ y: 100 }}
        transition={{ duration: 1 }}
        whileInView={{ y: 0 }}
        style={{ zIndex: 9999 }}
        ref={feedbackRef}
      >
        <Box p={2} borderRadius={1} bgcolor={"white"} display={'flex'} alignItems={'center'} position="relative">
          <IconButton
            onClick={onClose}
            sx={{ position: 'absolute', top: 5, right: 5, p: 0.5 }}
          >
            <CloseIcon sx={{ fontSize: '14px' }} />
          </IconButton>
          <Box>
            <IconButton>
              <AutoAwesomeOutlined sx={{ fontSize: '15px' }} />
            </IconButton>
          </Box>
          {questions.map((question, index) => (
            <Box key={index} px={1} mx={1}>
              <Typography variant="body2" mb={1} fontSize="14px">
                {question}
              </Typography>
              <StarRating
                rating={ratings[index]}
                onChange={(value) => handleRatingChange(index, value)}
              />
            </Box>
          ))}

          <Box pl={1}>
            <Button
              variant="outlined"
              sx={{
                fontSize: '14px',
                padding: '3px 7px',
                backgroundColor: 'white',
                border: '1px solid rgb(67, 131, 186)',
                color: 'black',
                '&:hover': {
                  background: "linear-gradient(to right, #04A05F 40%, #0D8C8D 60%, #1879BB 100%)",
                  color: 'white'
                },
              }}
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </Box>
        </Box>
      </motion.div>
    )
  ) : null;
}

export default Feedback;
